import type { ReactNode } from 'react';

/**
 * The billing slice's icon set, drawn inline.
 *
 * The console has no icon package, and the reference designs put a tinted tile
 * on every stat card and every navigation card in this section. Eighteen-pixel
 * strokes on `currentColor` let `StatCard` tint them from its `tone` without a
 * colour ever being written here.
 */
function svg(children: ReactNode): ReactNode {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      {children}
    </svg>
  );
}

export const IconLedger = (): ReactNode => svg(<>
  <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H19v16H5.5A1.5 1.5 0 0 1 4 18.5z" />
  <path d="M8 4v16" />
</>);

export const IconCredit = (): ReactNode => svg(<>
  <circle cx="12" cy="12" r="8.5" />
  <path d="M12 15.5V8.5M8.75 11.75 12 8.5l3.25 3.25" />
</>);

export const IconDebit = (): ReactNode => svg(<>
  <circle cx="12" cy="12" r="8.5" />
  <path d="M12 8.5v7M8.75 12.25 12 15.5l3.25-3.25" />
</>);

export const IconAdjustment = (): ReactNode => svg(<>
  <path d="M5 7h14M5 12h14M5 17h14" />
  <circle cx="9" cy="7" r="1.8" />
  <circle cx="15" cy="12" r="1.8" />
  <circle cx="10" cy="17" r="1.8" />
</>);

/** AI models (14 October 2026): a spark. */
export const IconSpark = (): ReactNode => svg(<>
  <path d="M10.5 3l1.9 5.1L17.5 10l-5.1 1.9-1.9 5.1-1.9-5.1L3.5 10l5.1-1.9z" />
  <path d="M18.5 14.5v6M15.5 17.5h6" />
</>);

export const IconTag = (): ReactNode => svg(<>
  <path d="M11.6 3.5H4.5a1 1 0 0 0-1 1v7.1a1 1 0 0 0 .3.7l8.4 8.4a1 1 0 0 0 1.4 0l7.1-7.1a1 1 0 0 0 0-1.4l-8.4-8.4a1 1 0 0 0-.7-.3z" />
  <circle cx="7.8" cy="7.8" r="1.4" />
</>);

export const IconInvoice = (): ReactNode => svg(<>
  <path d="M6 3h8l4 4v14H6z" />
  <path d="M14 3v4h4M9 12h6M9 16h4" />
</>);

export const IconWallet = (): ReactNode => svg(<>
  <path d="M3.5 8.5A2 2 0 0 1 5.5 6.5H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5.5a2 2 0 0 1-2-2z" />
  <path d="M3.5 8.5V7a2 2 0 0 1 2-2h9" />
  <circle cx="16.5" cy="13" r="1.2" />
</>);

export const IconGlobe = (): ReactNode => svg(<>
  <circle cx="12" cy="12" r="8.5" />
  <path d="M3.5 12h17M12 3.5c2.4 2.6 3.6 5.4 3.6 8.5s-1.2 5.9-3.6 8.5c-2.4-2.6-3.6-5.4-3.6-8.5s1.2-5.9 3.6-8.5z" />
</>);

export const IconTrend = (): ReactNode => svg(<>
  <path d="M3.5 16.5 9 11l3.5 3.5L20.5 6.5" />
  <path d="M15.5 6.5h5v5" />
</>);

export const IconClock = (): ReactNode => svg(<>
  <circle cx="12" cy="12" r="8.5" />
  <path d="M12 7.5V12l3 2" />
</>);

export const IconChart = (): ReactNode => svg(<>
  <path d="M4 20V4" />
  <path d="M8 20v-6M13 20V8M18 20v-9" />
</>);

export const IconLayers = (): ReactNode => svg(<>
  <path d="m12 3.5 8.5 4.5-8.5 4.5L3.5 8z" />
  <path d="m3.5 12.5 8.5 4.5 8.5-4.5" />
</>);

export const IconRepeat = (): ReactNode => svg(<>
  <path d="M4 10a6 6 0 0 1 6-6h7" />
  <path d="m14.5 1.5 3 2.5-3 2.5" />
  <path d="M20 14a6 6 0 0 1-6 6H7" />
  <path d="m9.5 22.5-3-2.5 3-2.5" />
</>);

export const IconRefresh = (): ReactNode => svg(<>
  <path d="M20 12a8 8 0 1 1-2.6-5.9" />
  <path d="M20.5 4v4.5H16" />
</>);

export const IconDownload = (): ReactNode => svg(<>
  <path d="M12 4v10" />
  <path d="m8 10.5 4 4 4-4" />
  <path d="M4.5 19.5h15" />
</>);

export const IconAlertFile = (): ReactNode => svg(<>
  <path d="M6 3h8l4 4v14H6z" />
  <path d="M14 3v4h4" />
  <path d="M12 11v3.5M12 17.5h.01" />
</>);

export const IconGateway = (): ReactNode => svg(<>
  <rect x="3" y="6" width="18" height="12" rx="2" />
  <path d="M3 10h18M7 14.5h3" />
</>);

export const IconPlus = (): ReactNode => svg(<>
  <path d="M12 5v14M5 12h14" />
</>);
